<template>
    <div class="bg-[radial-gradient(circle,rgba(240,238,246,1)_2px,transparent_2px)] bg-[length:44px_44px]">
        <Subheader :text="(odabraniRazred?.number ?? odabraniRazred?.order ?? '') + '. razred'" :vertical-margin="62">
            <div class="w-full max-w-4xl flex justify-center" data-nosnippet>
                <div class="bg-neutral-light-40 h-14 rounded-2xl flex items-center justify-center px-4">
                    <div class="flex items-center justify-center gap-6">
                        <span class="text-base font-sans font-normal text-neutral-dark-80">
                            Razred
                        </span>

                        <nav aria-label="Odabir razreda" class="flex">
                            <NuxtLink v-for="razred in razredi" :key="razred._id" :to="`/${razred.slug}`" class="relative w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-200 hover:scale-105" :class="[odabraniRazred?._id === razred._id ? 'bg-primary-100 text-neutral-light-100 segment-button-selected' : 'text-neutral-dark-80 hover:text-neutral-light-100']" :aria-current="odabraniRazred?._id === razred._id ? 'page' : undefined" :aria-label="`${razred.number ?? razred.order}. razred`">
                                <span class="text-base font-sans font-semibold ml-1">
                                    {{ razred.number ?? razred.order }}.
                                </span>
                            </NuxtLink>
                        </nav>
                    </div>
                </div>
            </div>
        </Subheader>

        <section aria-labelledby="grade-content-title">
            <div class="px-4 xl:px-56 py-8 sm:py-16 max-w-screen-2xl mx-auto relative">
                <h2 id="grade-content-title" class="sr-only">
                    Sadržaj razreda
                </h2>

                <div class="relative md:pb-16">
                    <div v-if="odabraniRazred?.hasActiveQuiz" class="mb-6 flex items-center justify-center">
                        <div v-if="hasMaxAccess" class="flex items-center relative">
                            <KwizardButton :disabled="loadingGradeQuestions" color="primary" size="large" class="inline-flex items-center" @click="handleGradeQuizClick">
                                <Icon name="kwizard:book-w-check" class="mr-3 h-6 w-6" />
                                
                                <span>Znanje razreda</span>
                            </KwizardButton>
                        </div>
                        
                        <div v-else class="flex relative group">
                            <div class="hidden lg:inline-flex items-center justify-center">
                                <NuxtLink ref="gradeButtonRef" :to="korisnikStore.korisnik ? '/cjenik' : '/prijava'" :aria-describedby="isGradeTooltipHovered ? gradeTooltipId : undefined" class="inline-flex items-center justify-center font-sans font-semibold rounded-lg transition-transform hover:scale-105 bg-primary-100 text-neutral-light-100 h-16 px-8 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-100 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-light-100" @mouseenter="handleGradeTooltipMouseEnter" @mouseleave="handleGradeTooltipMouseLeave" @focus="handleGradeTooltipMouseEnter" @blur="handleGradeTooltipMouseLeave">
                                    <NuxtImg src="/images/illustrations/lock.webp" width="18" height="27" densities="1" loading="lazy" decoding="async" alt="" aria-hidden="true" class="mr-4 ml-1 max-h-[27px] max-w-[18px] min-h-[27px] min-w-[18px] w-auto" />
                                    
                                    <span>Znanje razreda</span>
                                    
                                    <span class="sr-only">
                                        {{ korisnikStore.korisnik ? ' - potreban MAX paket' : ' - potrebna prijava i MAX paket' }}
                                    </span>
                                </NuxtLink>
                                
                                <div v-if="isGradeTooltipHovered" :id="gradeTooltipId" role="tooltip" class="hidden lg:block fixed transition-opacity duration-300 pointer-events-none z-[9999]" :style="gradeTooltipStyle">
                                    <div class="relative">
                                        <div class="bg-secondary-100 rounded-2xl shadow-[0px_4px_0px_0px_rgba(57,39,134,1.00)] px-5 py-4 w-80">
                                            <div class="text-primary-dark text-sm font-normal leading-5 font-sans">
                                                {{ gradeLockMessage }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="inline-flex lg:hidden items-center justify-center">
                                <KwizardButton color="primary" size="large" class="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-100 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-light-100" @click="showDialog = true">
                                    <NuxtImg src="/images/illustrations/lock.webp" width="18" height="27" densities="1" loading="lazy" decoding="async" alt="" aria-hidden="true" class="mr-4 ml-1 max-h-[27px] max-w-[18px] min-h-[27px] min-w-[18px] w-auto" />
                                    
                                    <span>Znanje razreda</span>
                                </KwizardButton>
                            </div>
                        </div>
                    </div>

                    <div class="bg-neutral-light-100 border-2 border-neutral-dark-6 rounded-3xl px-4 py-6 md:px-7" :aria-busy="predmetiLoading">
                        <Loading v-if="predmetiLoading" class="max-h-[320px]" role="status" aria-live="polite" />

                        <template v-else>
                            <LazyLiteratureGroup v-for="(predmet, index) in predmeti" :key="predmet._id" :predmet="predmet" :index="index" :odabrani-razred="odabraniRazred" :is-last="index === predmeti.length - 1" />
                        </template>
                    </div>
                </div>
            </div>
        </section>

        <LazyBagOfQuestions v-if="showGradeQuiz" :open="showGradeQuiz" :title="odabraniRazred?.name || ''" :questions="gradeQuestions" :used-key="`grade-${odabraniRazred?._id || ''}`" :quiz-label="'razreda'" @close="showGradeQuiz = false" @finished="onGradeQuizFinished" />

        <Dialog v-if="showDialog" v-model:open="showDialog" class="px-4">
            <DialogContent class="max-w-[90vw] sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Znanje razreda</DialogTitle>
                    <DialogDescription>{{ gradeLockMessage }}</DialogDescription>
                </DialogHeader>
                
                <DialogFooter class="flex gap-3 pt-2 sm:flex-row flex-row justify-end">
                    <DialogClose as-child>
                        <button type="button" class="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-100 hover:text-black">
                            Odustani
                        </button>
                    </DialogClose>

                    <NuxtLink :to="korisnikStore.korisnik ? '/cjenik' : '/prijava'" class="inline-flex items-center justify-center rounded-md bg-primary-100 px-4 py-2 text-sm font-medium text-neutral-light-100 transition-colors hover:bg-primary-100">
                        {{ korisnikStore.korisnik ? 'Pregledaj pakete' : 'Prijavi se' }}
                    </NuxtLink>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
    import { defineAsyncComponent } from 'vue';
    import type { Pitanje } from '~/types/pitanje';
    import type { Predmet } from '~/types/predmet';
    import type { Razred } from '~/types/razred';
    import baseSchema from '~/data/base-schema';

    function buildGradePageSchemaOrg(newRazred: Razred | null | undefined, predmetiData: Predmet[] | null | undefined) {
        if (!newRazred?.order || !newRazred?.slug || !predmetiData?.length) {
            return null;
        }

        const graph: any[] = [
            ...baseSchema,
            {
                '@id': `https://kwizard.hr/${newRazred.slug}`,
                '@type': ['WebPage', 'CollectionPage'],
                name: `${newRazred.order}. razred`,
                description: `Odabir literature za ${newRazred.order}. razred osnovne škole po predmetima.`,
                url: `https://kwizard.hr/${newRazred.slug}`,
                publisher: { '@id': 'https://kwizard.hr#organization' },
                inLanguage: 'hr',
                isPartOf: { '@id': 'https://kwizard.hr#website' },
                breadcrumb: { '@id': `https://kwizard.hr/${newRazred.slug}#breadcrumb` },
                mainEntity: { '@id': `https://kwizard.hr/${newRazred.slug}#subjects` }
            }
        ];

        graph.push({
            '@id': `https://kwizard.hr/${newRazred.slug}#subjects`,
            '@type': 'ItemList',
            name: `Predmeti za ${newRazred.order}. razred`,
            numberOfItems: predmetiData.length,
            itemListOrder: 'https://schema.org/ItemListUnordered',
            itemListElement: predmetiData.map((predmet, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: predmet.name,
                item: { '@id': `https://kwizard.hr/${newRazred.slug}/${predmet.slug}#course` }
            }))
        });

        for (const predmet of predmetiData) {
            const courseHasPart = (predmet.literature || []).map((literature) => ({
                '@id': `https://kwizard.hr/${newRazred.slug}#lit-${predmet.slug}-${literature.slug}`
            }));

            graph.push({
                '@id': `https://kwizard.hr/${newRazred.slug}/${predmet.slug}#course`,
                '@type': 'Course',
                name: `${predmet.name} - ${newRazred.order}. razred`,
                url: `https://kwizard.hr/${newRazred.slug}/${predmet.slug}`,
                description: `Gradivo predmeta ${predmet.name} za ${newRazred.order}. razred osnovne škole.`,
                educationalLevel: `Osnovna škola - ${newRazred.order}. razred`,
                inLanguage: 'hr',
                provider: { '@id': 'https://kwizard.hr#organization' },
                hasPart: courseHasPart.length > 0 ? courseHasPart : undefined
            });
        }

        for (const predmet of predmetiData) {
            for (const literature of predmet.literature || []) {
                const literatureUrl = `https://kwizard.hr/${newRazred.slug}/${predmet.slug}/${literature.slug}`;
                graph.push({
                    '@id': `https://kwizard.hr/${newRazred.slug}#lit-${predmet.slug}-${literature.slug}`,
                    '@type': 'LearningResource',
                    name: literature.name,
                    url: literatureUrl,
                    inLanguage: 'hr',
                    educationalLevel: `Osnovna škola - ${newRazred.order}. razred`,
                    teaches: predmet.name,
                    publisher: { '@id': 'https://kwizard.hr#organization' },
                    isPartOf: { '@id': `https://kwizard.hr/${newRazred.slug}/${predmet.slug}#course` }
                });
            }
        }

        graph.push({
            '@id': `https://kwizard.hr/${newRazred.slug}#breadcrumb`,
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Početna', item: 'https://kwizard.hr' },
                { '@type': 'ListItem', position: 2, name: `${newRazred.order}. razred`, item: `https://kwizard.hr/${newRazred.slug}` }
            ]
        });

        return {
            '@context': 'https://schema.org',
            '@graph': graph
        };
    }

    const razrediStore = useRazrediStore();
    const predmetiStore = usePredmetiStore();
    const korisnikStore = useKorisnikStore();
    const { $api } = useNuxtApp() as any;
    const route = useRoute();
    const LazyBagOfQuestions = defineAsyncComponent(() => import('~/components/BagOfQuestions.vue'));

    const { odabraniRazred, razredi } = storeToRefs(razrediStore);
    const { predmeti, isLoading: predmetiLoading } = storeToRefs(predmetiStore);

    const showGradeQuiz = ref(false);
    const loadingGradeQuestions = ref(false);
    const gradeQuestions = ref<Pitanje[]>([]);
    const showDialog = ref(false);

    const isGradeTooltipHovered = ref(false);
    const gradeTooltipPosition = ref({ top: 0, left: 0 });
    const gradeTooltipId = 'grade-lock-tooltip';

    const GRADE_TOOLTIP_WIDTH = 320;
    const GRADE_TOOLTIP_OFFSET = 8;

    type GradeButtonRef = HTMLElement | { $el?: HTMLElement } | null;
    const gradeButtonRef = ref<GradeButtonRef>(null);

    const getGradeButtonElement = () => {
        const rawRef = gradeButtonRef.value;
        if (!rawRef) return null;
        if (rawRef instanceof HTMLElement) return rawRef;
        return rawRef.$el instanceof HTMLElement ? rawRef.$el : null;
    };

    const updateGradeTooltipPosition = () => {
        const buttonElement = getGradeButtonElement();
        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;

        gradeTooltipPosition.value = {
            top: rect.bottom + GRADE_TOOLTIP_OFFSET,
            left: rect.left + rect.width / 2 - GRADE_TOOLTIP_WIDTH / 2
        };
    };

    const gradeTooltipStyle = computed(() => ({
        top: `${gradeTooltipPosition.value.top}px`,
        left: `${gradeTooltipPosition.value.left}px`
    }));

    const handleGradeTooltipMouseEnter = () => {
        updateGradeTooltipPosition();
        isGradeTooltipHovered.value = true;
    };

    const handleGradeTooltipMouseLeave = () => {
        isGradeTooltipHovered.value = false;
    };

    const hasMaxAccess = computed(() => korisnikStore.hasContentAccess);
    const gradeLockMessage = computed(() => (
        korisnikStore.korisnik
            ? 'Morate kupiti MAX paket da bi mogli provjeriti znanje razreda'
            : 'Morate biti registrirani i kupiti MAX paket da bi mogli provjeriti znanje razreda'
    ));

    const grades = {
        1: 'prvi',
        2: 'drugi',
        3: 'treći',
        4: 'četvrti',
        5: 'peti',
        6: 'šesti',
        7: 'sedmi',
        8: 'osmi'
    };

    const handleGradeQuizClick = async () => {
        if (!odabraniRazred.value) return;

        loadingGradeQuestions.value = true;
        try {
            const response = await $api('/api/quiz-questions', {
                query: {
                    grade: odabraniRazred.value.slug
                }
            });
            gradeQuestions.value = (response?.questions as Pitanje[]) ?? [];
            showGradeQuiz.value = true;
        } catch (error) {
            console.error('Error fetching grade quiz questions:', error);
            gradeQuestions.value = [];
        } finally {
            loadingGradeQuestions.value = false;
        }
    };

    const onGradeQuizFinished = () => {};

    const gradeDescription = computed(() => {
        const gradeNumber = odabraniRazred.value?.number as keyof typeof grades;
        const gradeName = grades[gradeNumber] ?? '';
        return `Sadržaj za ${gradeName} razred osnovne škole - prema kurikulumu ministarstva RH - Kreirali profesori`;
    });

    definePageMeta({
        layout: 'default',
        middleware: ['validate-razred']
    });

    const gradeSlug = computed(() => String(route.params.razred ?? ''));

    await useValidatedAsyncData(
        () => `grade-page-${gradeSlug.value}`,
        async () => {
            if (!gradeSlug.value) {
                throw createError({ statusCode: 404, message: 'Stranica nije pronađena' });
            }

            const razred = razrediStore.getRazredBySlug(gradeSlug.value);
            if (!razred) throw createError({ statusCode: 404, message: 'Stranica nije pronađena' });


            await razrediStore.setOdabraniRazred(gradeSlug.value);

            return true;
        },
        { watch: [gradeSlug] }
    );

    const schemaORG = import.meta.server
        ? buildGradePageSchemaOrg(odabraniRazred.value as Razred | undefined, predmeti.value as Predmet[] | undefined)
        : null;

    useSeoMeta({
        title: () => `${odabraniRazred.value?.number}. razred`,
        ogTitle: () => `${odabraniRazred.value?.number}. razred | Kwizard`,
        twitterTitle: () => `${odabraniRazred.value?.number}. razred | Kwizard`,
        description: () => gradeDescription.value,
        ogDescription: () => gradeDescription.value,
        twitterDescription: () => gradeDescription.value,
        ogSiteName: 'Kwizard',
        ogLocale: 'hr_HR',
        ogType: 'website',
        ogUrl: () => `https://kwizard.hr/${odabraniRazred.value?.slug ?? ''}`,
        ogImageAlt: 'Kwizard platforma',
        ogImage: 'https://kwizard.hr/images/branding/hero.png',
        twitterImage: 'https://kwizard.hr/images/branding/hero.png',
        twitterCard: 'summary_large_image'
    });

    useHead(() => ({
        meta: [
            { name: 'robots', content: 'index, follow' },
            {
                name: 'keywords',
                content: `${odabraniRazred.value?.number ?? ''}. razred, osnovna škola, predmeti, literatura, lekcije, kurikulum, ministarstvo, učenje, Kwizard, matematika, hrvatski jezik, priroda, povijest, geografija`
            }
        ],
        script: schemaORG
            ? [
                {
                    innerHTML: JSON.stringify(schemaORG),
                    key: 'schema-org-grade-page',
                    type: 'application/ld+json'
                }
            ]
            : []
    }));
</script>

<style scoped>
    .segment-button-selected {
        box-shadow: 0 4px 0 hsl(var(--primary-dark));
        transform: translateY(-2px);
    }
</style>
